<template>
  <teleport to="body">
    <div class="modal" v-if="open" @click.self.prevent="$emit('close')">
      <div class="wrapper">
        <div class="header">
          <font-awesome-icon icon="fa-xmark" class="exit" @click="$emit('close')" />
        </div>
        <div v-if="!showTerminalDetails" class="terminal-selector">
          <h2>Select a Payment Terminal</h2>
          <sub class="blue">Please select which payment terminal provider you would like to connect to.</sub>
          <div @click="selectTerminalProvider('windcave')" :class="terminalProviders.includes('windcave') ? 'selected' : ''">
            <img class="logo" src="/windcaveLogo.png" alt="" />
          </div>
          <div @click="selectTerminalProvider('eftpos_nz')" :class="terminalProviders.includes('eftpos_nz') ? 'selected' : ''">
            <img class="logo" src="/eftposNzLogo.webp" alt="" />
          </div>
          <div @click="selectTerminalProvider('smartpay')" :class="terminalProviders.includes('smartpay') ? 'selected' : ''">
            <img class="logo" src="/Smartpay_ideGJ3V3R7_0.png" alt="" />
          </div>
          <div @click="selectTerminalProvider('stripe')" :class="terminalProviders.includes('stripe') ? 'selected' : ''">
            <img class="logo" src="/stripeLogo.svg" alt="Stripe Logo" />
          </div>
          <p v-if="errorMsg" class="error-message">{{ errorMsg }}</p>
          <button class="confirm-provider" @click="confirmProviderSelection">Confirm</button>
        </div>

        <div v-if="showTerminalDetails" class="terminal-details">
          <h2>Connect to a Payment Terminal</h2>
          <p v-if="terminalProviders.length > 1" class="check-selection">
            If you have multiple terminal providers please ensure you select the terminal you want this station to connect to.
          </p>
          <section class="options">
            <div
              v-if="terminalProviders.includes('eftpos_nz')"
              @click.stop.prevent="useThisTerminal('eftpos_nz')"
              :class="selectedProvider === 'eftpos_nz' ? 'selected content' : 'content'">
              <h3>EFTPOS NZ:</h3>
              <div class="inputs">
                <label for="eftpos_ip">IP address:</label>
                <sub class="blue">This can be found on your EFTPOS terminal</sub>
                <input type="text" name="eftpos" id="eftpos_ip" v-model="eftposSettings.ip" />
                <label for="eftpos_port">Port:</label>
                <sub class="blue">Default is usually 4444</sub>
                <input type="text" name="eftpos" id="eftpos_port" v-model="eftposSettings.port" />
              </div>
              <button class="action-colour" @click="saveTerminalInfo('eftpos_nz')">Save</button>
            </div>

            <div
              v-if="terminalProviders.includes('windcave')"
              @click.stop.prevent="useThisTerminal('windcave')"
              :class="selectedProvider === 'windcave' ? 'selected content' : 'content'">
              <h3>Windcave:</h3>
              <div class="inputs">
                <label for="registerName">Register Name:</label>
                <sub class="blue">
                  OPTIONAL <br />
                </sub>
                <input type="text" name="registerName" id="registerName" v-model="registerName" placeholder="Register Name..." />
              </div>
              <button class="action-colour" @click="saveTerminalInfo('windcave')">Save</button>
            </div>

            <div
              v-if="terminalProviders.includes('stripe')"
              @click.stop.prevent="useThisTerminal('stripe')"
              :class="selectedProvider === 'stripe' ? 'selected content' : 'content'">
              <h3>Stripe:</h3>
              <button class="action-colour" v-if="!stripeReaders.length" @click.stop.prevent="handleUserSelection('stripe')">Scan for stripe readers</button>
              <select v-if="stripeReaders.length" name="readers" id="" class="dropdown" v-model="selectedReader">
                <option value="" selected>Select a Reader</option>
                <option :value="reader" v-for="reader in stripeReaders" :key="reader">{{ reader.deviceType }} - {{ reader.serialNumber }}</option>
              </select>
              <button v-if="stripeReaders.length" @click.stop.prevent="saveTerminalInfo('stripe')">Connect to reader</button>
            </div>

            <div
              v-if="terminalProviders.includes('smartpay')"
              @click.stop.prevent="useThisTerminal('smartpay')"
              :class="selectedProvider === 'smartpay' ? 'selected content' : 'content'">
              <h3>SmartPay:</h3>
              <div v-if="!localSettings[0].smartpay_terminal" class="inputs">
                <label for="pairingCode">Pairing code:</label>
                <sub class="blue">This can be found on your SmartPay terminal</sub>
                <input type="text" name="pairingCode" id="pairingCode" v-model="pairingCode" placeholder="Pairing code..." />
                <label for="registerName">Register Name:</label>
                <sub class="blue">
                  OPTIONAL <br />
                  Defaults to the name of your POS station</sub
                >
                <input type="text" name="registerName" id="registerName" v-model="registerName" placeholder="Register Name..." />
                <button class="action-colour" @click.stop.prevent="saveTerminalInfo('smartpay')">Pair with terminal</button>
              </div>

              <div v-else class="inputs">
                <p>SmartPay terminal is paired with {{ localSettings[0].smartpay_terminal.POSRegisterName }}</p>
                <sub class="blue">Initiate the unpairing process on the Terminal directly</sub>
                <sub class="blue">Once diconnected, remove SmartPay terminal provider</sub>
                <sub class="red">This should only be done if you are sure</sub>
              </div>
            </div>
          </section>

          <section class="action-btns">
            <button class="add-provider" @click="addTerminalProvider">Add/remove terminal provider</button>
          </section>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters } from "vuex";
import { LoadingSpinner } from "@monolith/components";
import Slug from "slug";

export default {
  name: "PaymentTerminalModal",
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      pairingCode: "",
      registerName: "",
      selectedReader: null,
      showConfirmModal: false,
      selectNewProvider: false,
      eftposSettings: {
        ip: "",
        port: "",
      },
      selectedProvider: null,
    };
  },
  props: ["open", "stripeReaders", "errorMsg"],
  emits: ["close", "eftposNZConnect", "stripeConnect", "smartpayConnect", "setAsTerminalProvider", "removeTerminalProvider", "selectProviders"],

  methods: {
    saveTerminalInfo(provider) {
      this.selectNewProvider = false;
      let regName = this.registerName ? `${this.registerName}-${this.localSettings[0].site}` : `${this.localSettings[0].site}-${this.localSettings[0].name}`;
      if (provider === "eftpos_nz") {
        this.$emit("eftposNZConnect", this.eftposSettings);
      }
      if (provider === "smartpay") {
        this.$emit("smartpayConnect", { pairingCode: this.pairingCode, registerName: Slug(regName) });
      }
      if (provider === "stripe") {
        this.$emit("stripeConnect", this.selectedReader);
      }
    },

    useThisTerminal(provider) {
      this.selectedProvider = provider;
      this.$emit("setAsTerminalProvider", provider);
    },

    selectTerminalProvider(provider) {
      let newProviders = this.terminalProviders;
      if (newProviders.includes(provider)) {
        newProviders.find((item, index) => {
          if (item === provider) newProviders.splice(index, 1);
        });
        this.$store.dispatch("setTerminalProviders", newProviders);
        this.saveSelectedTerminal(true, provider);
      } else {
        newProviders.push(provider);
        this.$store.dispatch("setTerminalProviders", newProviders);
        this.saveSelectedTerminal(provider);
      }
    },

    saveSelectedTerminal(remove = false, provider) {
      this.$emit("selectProviders");
      if (remove) {
        this.$emit("removeTerminalProvider", provider);
      }
    },

    confirmProviderSelection() {
      this.selectNewProvider = false;
      if (this.terminalProviders.length === 0) {
        this.$emit("close");
      }
    },

    addTerminalProvider() {
      this.selectNewProvider = true;
    },
  },

  watch: {
    localSettings: {
      handler() {
        if ("eftpos" in JSON.parse(localStorage.getItem("localSettings"))[0]) {
          this.eftposSettings.ip = this.localSettings[0]["eftpos"].ip || "";
          this.eftposSettings.port = this.localSettings[0]["eftpos"].port || "";
        }
        if ("active_terminal" in JSON.parse(localStorage.getItem("localSettings"))[0]) {
          this.selectedProvider = this.localSettings[0]["active_terminal"] || null;
        }
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters({
      localSettings: "localSettings",
      terminalProviders: "terminalProviders",
    }),

    showTerminalDetails() {
      return this.terminalProviders.length > 0 && !this.selectNewProvider;
    },
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";
.modal {
  margin: 0;

  .terminal-selector {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 1rem;
    .confirm-provider {
      width: 100%;
      background-color: var(--primary-colour);
      color: var(--action-colour);
      margin: 1rem 0;
    }
    .error-message {
      color: var(--warning-colour);
      font-weight: bold;
    }
    div {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: var(--primary-colour);
      border-radius: 1rem;
      box-shadow: var(--box-shadow);
      width: 100%;
      p {
        margin: 1rem;
      }
      .logo {
        width: 150px;
        margin: 1rem;
      }
      &.selected {
        background-color: var(--action-colour);
      }
      &:hover {
        cursor: pointer;
        img {
          transform: scale(1.05);
          transition: transform 0.2s;
        }
      }
    }
  }

  .wrapper {
    sub.blue {
      color: var(--action-colour);
      font-size: 0.7rem;
      margin: 5px 0;
    }
    sub.red {
      color: var(--warning-colour);
      font-size: 0.7rem;
      margin: 5px 0;
    }
    h2 {
      margin: 0.25rem;
    }
    display: flex;
    justify-content: center;
    align-items: center;
    .header {
      display: flex;
      justify-content: center;
      align-items: center;
      .exit {
        cursor: pointer;
      }
    }

    .terminal-details {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;

      .check-selection {
        font-size: 0.85rem;
        margin: 0 0 0.5rem 0;
        color: var(--action-colour);
      }

      .action-btns {
        display: flex;
        gap: 0.25rem;
        justify-content: center;
        align-items: center;

        .add-provider {
          border: none;
          color: var(--action-colour);
          background-color: none;
          cursor: pointer;
        }
      }
      .options {
        display: flex;
        gap: 1rem;
        justify-content: center;
        align-items: flex-start;
        .selected {
          border: 1px solid var(--action-colour);
        }
        .content {
          cursor: pointer;
          padding: 1rem 1.5rem;
          box-shadow: var(--box-shadow);
          border-radius: 0.5rem;
          background-color: var(--primary-colour);
          h3 {
            margin: 0.5rem 0;
          }
          .inputs {
            display: flex;
            flex-direction: column;
            label {
              margin-top: 10px;
            }
            input {
              padding: 5px;
              border: 1px solid #ccc;
              border-radius: 0.25rem;
              margin-bottom: 0.5rem !important;
            }
          }
        }
      }
    }
  }
}
</style>
